import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { invoiceSyncApi } from '../lib/api';
import { Header } from '../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { FileText, Download, RefreshCw, AlertTriangle, CheckCircle, Clock, Ban, Bell } from 'lucide-react';
import { useInvoiceReminder } from '../hooks/useInvoiceReminder';

const STATUS_STYLE = {
    paid: { className: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle },
    unpaid: { className: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    overdue: { className: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle },
};

function InvoiceStatusBadge({ status, daysOverdue }) {
    const { t } = useTranslation();
    const style = STATUS_STYLE[status] || STATUS_STYLE.unpaid;
    const Icon = style.icon;
    const label = status === 'paid'
        ? t('invoices.status_paid', 'Paid')
        : status === 'overdue'
            ? t('invoices.status_overdue', 'Overdue')
            : t('invoices.status_unpaid', 'Unpaid');
    return (
        <div className="flex flex-col items-center gap-0.5">
            <Badge data-testid={`invoice-status-${status}`} className={`${style.className} border font-medium gap-1`}>
                <Icon className="h-3 w-3" />
                {label}
            </Badge>
            {status === 'overdue' && daysOverdue != null && (
                <span className="text-[10px] text-red-500 font-medium" data-testid="days-overdue">
                    {t('invoices.days_overdue', '{n}d overdue', { n: daysOverdue })}
                </span>
            )}
        </div>
    );
}

function formatCurrency(amount, currency = 'DKK') {
    if (amount == null) return '-';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function CustomerInvoicesPage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [invoices, setInvoices] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const [filter, setFilter] = useState('all');
    // Shared reminder modal + logic. Hooked up so a successful send
    // refreshes the invoices list to pick up the new
    // `last_reminder_sent_at` timestamp.
    const { openReminderModal, ReminderModal } = useInvoiceReminder({
        onSent: () => fetchInvoices(),
    });
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    const orgId = user?.active_company_id || user?.organization_id;

    const fetchInvoices = useCallback(async () => {
        if (!orgId) return;
        setLoading(true);
        try {
            const statusParam = filter !== 'all' ? filter : undefined;
            const { data } = await invoiceSyncApi.getInvoices(orgId, statusParam);
            setInvoices(data.invoices || []);
            setSummary({
                outstanding: data.outstanding_balance,
                overdue: data.overdue_balance,
                creditLimit: data.credit_limit,
                currency: data.currency || 'DKK',
                lastSync: data.last_sync,
                orgName: data.organization_name,
                totalCount: data.total_count,
                oldestOverdueDays: data.oldest_overdue_days,
            });
        } catch (err) {
            console.error('Failed to load invoices', err);
        } finally {
            setLoading(false);
        }
    }, [orgId, filter]);

    useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

    const handleDownloadPdf = async (invoiceNumber) => {
        setDownloading(invoiceNumber);
        try {
            const response = await invoiceSyncApi.getInvoicePdf(orgId, invoiceNumber);
            const blob = response.data;
            
            if (blob.type === 'application/json') {
                // Fallback: backend returned JSON with download_url
                const text = await blob.text();
                const json = JSON.parse(text);
                if (json.download_url) {
                    window.open(json.download_url, '_blank');
                    return;
                }
                toast.error(json.detail || t('invoices.pdf_not_available', 'PDF not available'));
                return;
            }
            
            // Open PDF blob in new tab
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            const detail = err.response?.data instanceof Blob
                ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
                : err.response?.data?.detail;
            toast.error(detail || t('invoices.pdf_download_error', 'Could not download invoice PDF'));
        } finally {
            setDownloading(null);
        }
    };

    const handleSync = async () => {
        try {
            toast.info(t('invoices.syncing', 'Syncing invoices from e-conomic...'));
            await invoiceSyncApi.triggerSync(orgId);
            toast.success(t('invoices.sync_success', 'Invoices synced successfully'));
            fetchInvoices();
        } catch (err) {
            toast.error(err.response?.data?.detail || t('invoices.sync_error', 'Sync failed'));
        }
    };

    const creditBlocked = summary.creditLimit && summary.outstanding > summary.creditLimit;

    const filterLabel = (f) => {
        if (f === 'all') return t('invoices.filter_all', 'All');
        if (f === 'unpaid') return t('invoices.filter_unpaid', 'Unpaid');
        if (f === 'overdue') return t('invoices.filter_overdue', 'Overdue');
        if (f === 'paid') return t('invoices.filter_paid', 'Paid');
        return f;
    };

    return (
        <div className="min-h-screen bg-background" data-testid="customer-invoices-page">
            <Header />
            <main className="max-w-6xl mx-auto p-6 space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{t('invoices.title', 'Invoices & Billing')}</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        {summary.orgName || t('invoices.your_company', 'Your company')} — {t('invoices.subtitle', 'Invoice overview from e-conomic')}
                    </p>
                </div>
                {isAdmin && (
                    <Button variant="outline" size="sm" onClick={handleSync} data-testid="sync-invoices-btn">
                        <RefreshCw className="h-4 w-4 mr-2" />
                        {t('invoices.sync_button', 'Sync with e-conomic')}
                    </Button>
                )}
            </div>

            {/* Credit Warning */}
            {creditBlocked && (
                <Card className="border-red-300 bg-red-50" data-testid="credit-blocked-warning">
                    <CardContent className="flex items-center gap-3 py-4">
                        <Ban className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div>
                            <p className="font-medium text-red-800">{t('invoices.credit_blocked_title', 'Order submission blocked')}</p>
                            <p className="text-sm text-red-600">
                                {t(
                                    'invoices.credit_blocked_desc',
                                    'Outstanding balance ({outstanding}) exceeds credit limit ({limit}). Please settle outstanding invoices or contact your account manager.',
                                    {
                                        outstanding: formatCurrency(summary.outstanding, summary.currency),
                                        limit: formatCurrency(summary.creditLimit, summary.currency),
                                    }
                                )}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card data-testid="outstanding-balance-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('invoices.card_outstanding', 'Outstanding Balance')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{formatCurrency(summary.outstanding, summary.currency)}</p>
                    </CardContent>
                </Card>
                <Card data-testid="overdue-balance-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('invoices.card_overdue', 'Overdue')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className={`text-2xl font-bold ${summary.overdue > 0 ? 'text-red-600' : ''}`}>
                            {formatCurrency(summary.overdue, summary.currency)}
                        </p>
                        {summary.oldestOverdueDays > 0 && (
                            <p className="text-xs text-red-500 mt-1" data-testid="oldest-overdue-summary">
                                {t('invoices.oldest_overdue', 'Oldest: {n} days overdue', { n: summary.oldestOverdueDays })}
                            </p>
                        )}
                    </CardContent>
                </Card>
                <Card data-testid="credit-limit-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">{t('invoices.card_credit_limit', 'Credit Limit')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">
                            {summary.creditLimit ? formatCurrency(summary.creditLimit, summary.currency) : t('invoices.no_credit_limit', 'No limit')}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2" data-testid="invoice-filter-tabs">
                {['all', 'unpaid', 'overdue', 'paid'].map((f) => (
                    <Button
                        key={f}
                        variant={filter === f ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setFilter(f)}
                        data-testid={`filter-${f}`}
                    >
                        {filterLabel(f)}
                        {f === 'all' && summary.totalCount ? ` (${summary.totalCount})` : ''}
                    </Button>
                ))}
            </div>

            {/* Invoice Table */}
            <Card>
                <CardContent className="p-0">
                    {loading ? (
                        <div className="p-8 text-center text-muted-foreground">{t('invoices.loading', 'Loading invoices...')}</div>
                    ) : invoices.length === 0 ? (
                        <div className="p-8 text-center text-muted-foreground" data-testid="no-invoices">
                            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p>{t('invoices.empty', 'No invoices found')}</p>
                            {!summary.lastSync && isAdmin && (
                                <p className="text-xs mt-2">{t('invoices.empty_sync_hint', 'Click "Sync with e-conomic" to fetch invoices')}</p>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full" data-testid="invoices-table">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_number', 'Invoice #')}</th>
                                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_date', 'Date')}</th>
                                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_due_date', 'Due Date')}</th>
                                        <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_reference', 'Reference')}</th>
                                        <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_amount', 'Amount')}</th>
                                        <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_outstanding', 'Outstanding')}</th>
                                        <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_status', 'Status')}</th>
                                        <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">{t('invoices.col_pdf', 'PDF')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => (
                                        <tr key={inv.id || inv.economic_invoice_number} className="border-b last:border-0 hover:bg-muted/30" data-testid={`invoice-row-${inv.economic_invoice_number}`}>
                                            <td className="p-3 font-medium">{inv.economic_invoice_number}</td>
                                            <td className="p-3 text-sm">{formatDate(inv.date)}</td>
                                            <td className="p-3 text-sm">{formatDate(inv.due_date)}</td>
                                            <td className="p-3 text-sm text-muted-foreground">{inv.references_other || '-'}</td>
                                            <td className="p-3 text-sm text-right font-medium">{formatCurrency(inv.gross_amount, inv.currency)}</td>
                                            <td className="p-3 text-sm text-right font-medium">
                                                {inv.remainder > 0 ? formatCurrency(inv.remainder, inv.currency) : '-'}
                                            </td>
                                            <td className="p-3 text-center">
                                                <InvoiceStatusBadge status={inv.payment_status} daysOverdue={inv.days_overdue} />
                                                {inv.payment_status === 'overdue' && inv.last_reminder_sent_at && (
                                                    <div className="text-[10px] text-slate-500 mt-1" data-testid={`last-reminder-${inv.economic_invoice_number}`} title={`Sent by ${inv.last_reminder_sent_by_name || 'admin'}`}>
                                                        {t('invoices.last_reminder', 'Reminder sent {date}', { date: formatDate(inv.last_reminder_sent_at) })}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    {isAdmin && inv.payment_status === 'overdue' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openReminderModal(inv)}
                                                            data-testid={`send-reminder-${inv.economic_invoice_number}`}
                                                            aria-label={t('invoices.send_reminder', 'Send reminder')}
                                                            title={t('invoices.send_reminder', 'Send reminder')}
                                                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        >
                                                            <Bell className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleDownloadPdf(inv.economic_invoice_number)}
                                                        disabled={downloading === inv.economic_invoice_number}
                                                        data-testid={`download-pdf-${inv.economic_invoice_number}`}
                                                        aria-label={t('invoices.download_pdf', 'Download PDF')}
                                                        title={t('invoices.download_pdf', 'Download PDF')}
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Last Sync Info */}
            {summary.lastSync && (
                <p className="text-xs text-muted-foreground text-right" data-testid="last-sync-info">
                    {t(
                        'invoices.last_synced',
                        'Last synced: {date} at {time}',
                        {
                            date: formatDate(summary.lastSync),
                            time: new Date(summary.lastSync).toLocaleTimeString('da-DK'),
                        }
                    )}
                </p>
            )}


            {/* Payment-Reminder modal — shared with the admin
                Companies → Invoices & Credit tab via useInvoiceReminder(). */}
            {ReminderModal}

            </main>
        </div>
    );
}
